import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Cpu, Sun, CreditCard, Shield, DollarSign, Play, RefreshCw,
  Clock, CheckCircle2, AlertTriangle, XCircle, Activity,
  Calendar, Loader2, AlertCircle, Zap, BarChart3, TrendingUp,
  FileWarning, Landmark, Calculator, TrendingDown, Building2, Briefcase,
  Boxes, Home, Gavel, PieChart, Banknote
} from 'lucide-react';
import { cn } from '../lib/utils';
import { useAuth } from '../contexts/AuthContext';

// Types
interface AgentInfo {
  agentId: string;
  agentName: string;
  description: string;
  schedule: string;
  hasLatestReport: boolean;
  status?: string;
  lastRunStatus?: string;
  lastRunAt?: string | null;
  nextRunLabel?: string | null;
  lastError?: string | null;
}
interface AgentStatusResponse {
  tenantId: number; timestamp: string; agents: AgentInfo[];
  activeAgents: number; totalAgents: number;
}
interface RegulatoryStatus { violations: number; criticalViolations: number; warnings: number; upcomingDeadlines: number; scanTimestamp: string; }
interface CashOptimizationStatus { totalUninvestedCash: number; aggregateCashPercent: number; opportunities: number; potentialYieldPickup: number; analysisTimestamp: string; }
interface DashboardResponse { tenantId: number; timestamp: string; regulatory: RegulatoryStatus; cashOptimization: CashOptimizationStatus; healthScore: number; healthStatus: 'GOOD' | 'WARNING' | 'CRITICAL'; }
interface AlertItem { severity: string; category: string; title: string; description: string; }
interface ActionItem { priority: string; category: string; title: string; description: string; }
interface MorningBriefing { generatedAt: string; marketDate: string; executiveSummary: string; marketSummary: any; portfolioBriefings: any[]; actionItems: ActionItem[]; alerts: AlertItem[]; getTotalAlerts?: () => number; }
interface SettlementItem { tradeId: number; securityName: string; status: string; settlementAmount: number; }
interface SettlementReport { generatedAt: string; executiveSummary: string; pendingSettlements: SettlementItem[]; settledToday: SettlementItem[]; failedTrades: SettlementItem[]; atRiskTrades: Array<{ trade: SettlementItem; riskScore: number; riskFactors: string[]; suggestedActions: string[] }>; totalPendingValue: number; totalAtRiskValue: number; overallRiskScore: number; }
interface RegulatoryReport { violations: Array<{ ruleName: string; severity: string; description: string }>; upcomingDeadlines: Array<{ reportType: string; deadline: string; status: string }>; }
interface CashOptimizationReport { opportunities: Array<{ portfolioName: string; opportunityType: string; description: string; amount: number; expectedReturn: number; securityType: string; }>; totalUninvestedCash: number; potentialYieldPickup: number; }

// PC agent report types
interface CovenantReport { executiveSummary: string; overdueCount: number; dueSoonCount: number; items: any[]; generatedAt: string; }
interface DrawdownReport { executiveSummary: string; recentActivityCount: number; expiringDdtlCount: number; highUnfundedCount: number; recentActivity: any[]; expiringDdtls: any[]; highUnfundedCommitments: any[]; generatedAt: string; }
interface AccrualReport { executiveSummary: string; missingAccrualCount: number; pikEventCount: number; nonAccrualCount: number; missingAccruals: any[]; pikEvents: any[]; nonAccrualLoans: any[]; generatedAt: string; }
interface DeteriorationReport { executiveSummary: string; totalOnWatchlist: number; criticalCount: number; highCount: number; watchlist: any[]; generatedAt: string; }

// Status Badge
const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const config: Record<string, { bg: string; text: string; icon: React.ReactNode }> = {
    GOOD: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', icon: <CheckCircle2 className="w-3 h-3" /> },
    WARNING: { bg: 'bg-amber-500/20', text: 'text-amber-400', icon: <AlertTriangle className="w-3 h-3" /> },
    CRITICAL: { bg: 'bg-red-500/20', text: 'text-red-400', icon: <XCircle className="w-3 h-3" /> },
    READY: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', icon: <CheckCircle2 className="w-3 h-3" /> },
    SUCCESS: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', icon: <CheckCircle2 className="w-3 h-3" /> },
    PENDING: { bg: 'bg-slate-500/20', text: 'text-slate-400', icon: <Clock className="w-3 h-3" /> },
    NEVER_RUN: { bg: 'bg-slate-500/20', text: 'text-slate-400', icon: <Clock className="w-3 h-3" /> },
    ERROR: { bg: 'bg-red-500/20', text: 'text-red-400', icon: <XCircle className="w-3 h-3" /> },
    true: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', icon: <CheckCircle2 className="w-3 h-3" /> },
    false: { bg: 'bg-slate-500/20', text: 'text-slate-400', icon: <Clock className="w-3 h-3" /> },
  };
  const { bg, text, icon } = config[status] || config.GOOD;
  return (
    <span className={cn('inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium', bg, text)}>
      {icon}{status === 'true' ? 'Ready' : status === 'false' ? 'Pending' : status.replace('_', ' ')}
    </span>
  );
};

const SeverityBadge: React.FC<{ severity: string }> = ({ severity }) => {
  const config: Record<string, { bg: string; text: string }> = {
    HIGH: { bg: 'bg-red-500/20', text: 'text-red-400' }, CRITICAL: { bg: 'bg-red-500/20', text: 'text-red-400' },
    MEDIUM: { bg: 'bg-amber-500/20', text: 'text-amber-400' }, WARNING: { bg: 'bg-amber-500/20', text: 'text-amber-400' },
    LOW: { bg: 'bg-blue-500/20', text: 'text-blue-400' }, INFO: { bg: 'bg-blue-500/20', text: 'text-blue-400' },
  };
  const { bg, text } = config[severity] || config.LOW;
  return <span className={cn('px-2 py-0.5 rounded text-xs font-medium', bg, text)}>{severity}</span>;
};

// Agent Card
const formatAgentRunTime = (value?: string | null) => {
  if (!value) return 'No report yet';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? value
    : parsed.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const AgentCard: React.FC<{ agent: AgentInfo; icon: React.ReactNode; color: string; onTrigger: () => void; loading: boolean; }> = ({ agent, icon, color, onTrigger, loading }) => (
  <motion.div className="bg-slate-900 border border-white/10 rounded-xl p-4 hover:border-white/20 transition-all" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
    <div className="flex items-start justify-between mb-3">
      <div className="flex items-center gap-3">
        <div className={cn('w-10 h-10 rounded-lg flex items-center justify-center', color)}>{icon}</div>
        <div>
          <h3 className="font-medium text-white text-sm">{agent.agentName}</h3>
          <StatusBadge status={agent.status || agent.lastRunStatus || String(agent.hasLatestReport)} />
        </div>
      </div>
      <button onClick={onTrigger} disabled={loading} className={cn('p-2 rounded-lg transition-all', loading ? 'bg-slate-800 text-slate-500 cursor-not-allowed' : 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30')} title="Run Now">
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
      </button>
    </div>
    <p className="text-xs text-slate-400 mb-3 line-clamp-2">{agent.description}</p>
    <div className="bg-slate-800/50 rounded-lg p-2">
      <div className="text-slate-500 text-[10px] mb-0.5">Schedule</div>
      <div className="text-slate-300 text-xs font-mono">{agent.nextRunLabel || agent.schedule.split(' (')[1]?.replace(')', '') || agent.schedule}</div>
      <div className="mt-1 text-slate-500 text-[10px]">Last run</div>
      <div className="text-slate-300 text-xs">{formatAgentRunTime(agent.lastRunAt)}</div>
      {agent.lastError && <div className="mt-1 text-red-300 text-xs line-clamp-2">{agent.lastError}</div>}
    </div>
  </motion.div>
);

// AI Summary Panel — shared by all 4 PC agents
const AISummaryPanel: React.FC<{
  icon: React.ReactNode; iconColor: string; title: string; summary: string | null;
  metrics: Array<{ label: string; value: number | string; color?: string }>;
  agentId: string; onTrigger: () => void; triggering: boolean;
  emptyMessage: string; schedule: string;
}> = ({ icon, iconColor, title, summary, metrics, agentId, onTrigger, triggering, emptyMessage, schedule }) => {
  if (!summary) {
    return (
      <div className="bg-slate-900 border border-white/10 rounded-xl p-8 text-center">
        <div className={cn('w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3', iconColor.replace('text-', 'bg-').replace('-400', '-500/20'))}>{icon}</div>
        <p className="text-slate-400">{emptyMessage}</p>
        <p className="text-sm text-slate-500 mt-1">Scheduled: {schedule}</p>
        <button onClick={onTrigger} disabled={triggering} className="mt-4 px-4 py-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-all">
          {triggering ? <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> : <Play className="w-4 h-4 inline mr-2" />}
          Run Now
        </button>
      </div>
    );
  }

  return (
    <motion.div key={agentId} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
      {/* Key counts */}
      <div className={cn('grid gap-4 mb-4', `grid-cols-${Math.min(metrics.length, 4)}`)}>
        {metrics.map((m, i) => (
          <div key={i} className="bg-slate-900 border border-white/10 rounded-xl p-4">
            <div className={cn('text-2xl font-bold', m.color || 'text-white')}>{m.value}</div>
            <div className="text-xs text-slate-500 mt-1">{m.label}</div>
          </div>
        ))}
      </div>

      {/* AI Summary */}
      <div className="bg-slate-900 border border-white/10 rounded-xl p-5">
        <div className="flex items-center gap-2 mb-3">
          <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center', iconColor.replace('text-', 'bg-').replace('-400', '-500/20'))}>
            {icon}
          </div>
          <div>
            <h3 className="text-white font-medium text-sm">{title}</h3>
            <span className="text-[10px] text-slate-500 uppercase tracking-wider">AI Executive Summary</span>
          </div>
          <button onClick={onTrigger} disabled={triggering} className="ml-auto p-1.5 rounded-lg bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 transition-all" title="Refresh">
            {triggering ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          </button>
        </div>
        <div className="bg-slate-800/40 rounded-lg p-4 border border-white/5">
          <p className="text-slate-200 text-sm leading-relaxed whitespace-pre-line">{summary}</p>
        </div>
      </div>
    </motion.div>
  );
};

// Main Dashboard
const AutonomousAgentsDashboard: React.FC = () => {
  const { token } = useAuth();
  const [dashboard, setDashboard] = useState<DashboardResponse | null>(null);
  const [agentStatus, setAgentStatus] = useState<AgentStatusResponse | null>(null);
  const [morningBriefing, setMorningBriefing] = useState<MorningBriefing | null>(null);
  const [settlementReport, setSettlementReport] = useState<SettlementReport | null>(null);
  const [regulatoryReport, setRegulatoryReport] = useState<RegulatoryReport | null>(null);
  const [cashReport, setCashReport] = useState<CashOptimizationReport | null>(null);
  // PC agent reports
  const [covenantReport, setCovenantReport] = useState<CovenantReport | null>(null);
  const [drawdownReport, setDrawdownReport] = useState<DrawdownReport | null>(null);
  const [accrualReport, setAccrualReport] = useState<AccrualReport | null>(null);
  const [deteriorationReport, setDeteriorationReport] = useState<DeteriorationReport | null>(null);
  // New asset-class monitors (Credit & Loans + PE/RE), keyed by agentId
  const [monitorReports, setMonitorReports] = useState<Record<string, any>>({});

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [triggeringAgent, setTriggeringAgent] = useState<string | null>(null);

  // Top-level tab: portfolio | alternatives | credit-loans
  const [topTab, setTopTab] = useState<'portfolio' | 'alternatives' | 'credit-loans'>('portfolio');
  // Portfolio subtabs
  const [portfolioTab, setPortfolioTab] = useState<'overview' | 'briefing' | 'settlement' | 'regulatory' | 'cash'>('overview');
  // Alternatives subtabs (PC + PE/RE)
  const [altTab, setAltTab] = useState<'covenant' | 'drawdown' | 'accrual' | 'deterioration' | 'pe-capital-call-monitor' | 'pe-pacing-monitor' | 're-debt-monitor' | 're-equity-monitor'>('covenant');
  // Credit & Loans subtabs
  const [creditTab, setCreditTab] = useState<string>('abf-surveillance-monitor');

  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [dashboardRes, statusRes] = await Promise.all([
        fetch('/api/autonomous-workflows/dashboard', { headers }),
        fetch('/api/autonomous-workflows/status', { headers })
      ]);
      if (dashboardRes.ok) setDashboard(await dashboardRes.json());
      if (statusRes.ok) setAgentStatus(await statusRes.json());

      // Portfolio agent reports
      const tryFetch = async (url: string, setter: (d: any) => void) => {
        try {
          const r = await fetch(url, { headers });
          if (r.ok) {
            const data = await r.json();
            if (data) setter(data);
          }
        } catch (e) {
          console.debug(`Failed to fetch ${url}:`, e);
        }
      };
      await Promise.all([
        tryFetch('/api/autonomous-workflows/morning-briefing/latest', setMorningBriefing),
        tryFetch('/api/autonomous-workflows/settlement/latest', setSettlementReport),
        tryFetch('/api/autonomous-workflows/regulatory/latest', setRegulatoryReport),
        tryFetch('/api/autonomous-workflows/cash-optimization/latest', setCashReport),
        // PC agent reports
        tryFetch('/api/autonomous-workflows/covenant-deadline-watchdog/latest', (d) => setCovenantReport(d.report || d)),
        tryFetch('/api/autonomous-workflows/drawdown-funding-monitor/latest', (d) => setDrawdownReport(d.report || d)),
        tryFetch('/api/autonomous-workflows/accrual-pik-reconciler/latest', (d) => setAccrualReport(d.report || d)),
        tryFetch('/api/autonomous-workflows/borrower-deterioration-sweep/latest', (d) => setDeteriorationReport(d.report || d)),
        // New asset-class monitors (Credit & Loans + PE/RE)
        ...['abf-surveillance-monitor', 'clo-surveillance', 'distressed-deadline-monitor', 'cmbs-surveillance', 'll-maturity-monitor', 'cds-roll-monitor',
            'pe-capital-call-monitor', 'pe-pacing-monitor', 're-debt-monitor', 're-equity-monitor'].map(id =>
          tryFetch(`/api/autonomous-workflows/${id}/latest`, (d) => setMonitorReports(prev => ({ ...prev, [id]: d.report || d })))),
      ]);
      setError(null);
    } catch (err) {
      setError('Failed to load autonomous agents dashboard');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) { fetchData(); const i = setInterval(fetchData, 60000); return () => clearInterval(i); }
  }, [fetchData, token]);

  const triggerAgent = async (agentId: string) => {
    try {
      setTriggeringAgent(agentId);
      const res = await fetch(`/api/autonomous-workflows/${agentId}/trigger`, { method: 'POST', headers });
      if (!res.ok) throw new Error('Failed');
      setTimeout(fetchData, 8000);
    } catch (err) { console.error(err); } finally { setTriggeringAgent(null); }
  };

  const agentConfig: Record<string, { icon: React.ReactNode; color: string }> = {
    'morning-briefing':          { icon: <Sun className="w-5 h-5 text-amber-400" />,     color: 'bg-amber-500/20' },
    'settlement':                { icon: <CreditCard className="w-5 h-5 text-blue-400" />, color: 'bg-blue-500/20' },
    'regulatory-watchdog':       { icon: <Shield className="w-5 h-5 text-violet-400" />,  color: 'bg-violet-500/20' },
    'cash-optimization':         { icon: <DollarSign className="w-5 h-5 text-emerald-400" />, color: 'bg-emerald-500/20' },
    'covenant-deadline-watchdog':{ icon: <FileWarning className="w-5 h-5 text-orange-400" />, color: 'bg-orange-500/20' },
    'drawdown-funding-monitor':  { icon: <Landmark className="w-5 h-5 text-cyan-400" />,  color: 'bg-cyan-500/20' },
    'accrual-pik-reconciler':    { icon: <Calculator className="w-5 h-5 text-purple-400" />, color: 'bg-purple-500/20' },
    'borrower-deterioration-sweep': { icon: <TrendingDown className="w-5 h-5 text-rose-400" />, color: 'bg-rose-500/20' },
    // Credit & Loans monitors
    'abf-surveillance-monitor':     { icon: <Briefcase className="w-5 h-5 text-teal-400" />,    color: 'bg-teal-500/20' },
    'clo-surveillance':            { icon: <Boxes className="w-5 h-5 text-cyan-400" />,      color: 'bg-cyan-500/20' },
    'distressed-deadline-monitor': { icon: <Gavel className="w-5 h-5 text-rose-400" />,      color: 'bg-rose-500/20' },
    'cmbs-surveillance':           { icon: <Building2 className="w-5 h-5 text-amber-400" />, color: 'bg-amber-500/20' },
    'll-maturity-monitor':         { icon: <Landmark className="w-5 h-5 text-sky-400" />,    color: 'bg-sky-500/20' },
    'cds-roll-monitor':            { icon: <Shield className="w-5 h-5 text-violet-400" />,   color: 'bg-violet-500/20' },
    // Alternatives (PE + RE) monitors
    'pe-capital-call-monitor':     { icon: <Banknote className="w-5 h-5 text-emerald-400" />, color: 'bg-emerald-500/20' },
    'pe-pacing-monitor':           { icon: <PieChart className="w-5 h-5 text-indigo-400" />,  color: 'bg-indigo-500/20' },
    're-debt-monitor':             { icon: <Home className="w-5 h-5 text-orange-400" />,      color: 'bg-orange-500/20' },
    're-equity-monitor':           { icon: <Building2 className="w-5 h-5 text-teal-400" />,   color: 'bg-teal-500/20' },
  };

  const PORTFOLIO_IDS = ['morning-briefing', 'settlement', 'regulatory-watchdog', 'cash-optimization'];
  const ALTERNATIVES_IDS = ['covenant-deadline-watchdog', 'drawdown-funding-monitor', 'accrual-pik-reconciler', 'borrower-deterioration-sweep',
    'pe-capital-call-monitor', 'pe-pacing-monitor', 're-debt-monitor', 're-equity-monitor'];
  const CREDIT_IDS = ['abf-surveillance-monitor', 'clo-surveillance', 'distressed-deadline-monitor', 'cmbs-surveillance', 'll-maturity-monitor', 'cds-roll-monitor'];

  const portfolioAgents = agentStatus?.agents?.filter(a => PORTFOLIO_IDS.includes(a.agentId)) ?? [];
  const alternativesAgents = agentStatus?.agents?.filter(a => ALTERNATIVES_IDS.includes(a.agentId)) ?? [];
  const creditAgents = agentStatus?.agents?.filter(a => CREDIT_IDS.includes(a.agentId)) ?? [];

  // Config-driven monitors (the 9 new asset-class agents)
  type Metric = { label: string; field: string; tone?: 'bad' | 'warn' };
  type MonitorCfg = { id: string; tab: 'alternatives' | 'credit-loans'; title: string; iconColor: string; schedule: string; metrics: Metric[] };
  const MONITORS: MonitorCfg[] = [
    { id: 'abf-surveillance-monitor', tab: 'credit-loans', title: 'ABF Surveillance Monitor', iconColor: 'text-teal-400', schedule: '6:45 AM ET weekdays',
      metrics: [{ label: 'Facilities', field: 'facilitiesMonitored' }, { label: 'Low Avail', field: 'lowAvailabilityCount', tone: 'warn' }, { label: 'Test Exceptions', field: 'advanceRateExceptionCount', tone: 'bad' }, { label: 'Stale BBC', field: 'staleBorrowingBaseCount', tone: 'warn' }] },
    { id: 'clo-surveillance', tab: 'credit-loans', title: 'CLO Surveillance', iconColor: 'text-cyan-400', schedule: '7:00 AM ET weekdays',
      metrics: [{ label: 'Deals', field: 'dealsMonitored' }, { label: 'High CCC', field: 'highCccCount', tone: 'bad' }, { label: 'Defaulted', field: 'defaultedCount', tone: 'bad' }, { label: 'Reinvest End', field: 'reinvestmentEndingSoonCount', tone: 'warn' }] },
    { id: 'distressed-deadline-monitor', tab: 'credit-loans', title: 'Distressed Deadline Monitor', iconColor: 'text-rose-400', schedule: '7:15 AM ET weekdays',
      metrics: [{ label: 'Active Situations', field: 'activeSituations' }, { label: 'Imminent (≤30d)', field: 'imminentDeadlineCount', tone: 'warn' }] },
    { id: 'cmbs-surveillance', tab: 'credit-loans', title: 'CMBS Surveillance', iconColor: 'text-amber-400', schedule: '7:30 AM ET weekdays',
      metrics: [{ label: 'Pools', field: 'poolsMonitored' }, { label: 'High Delinq', field: 'highDelinquencyCount', tone: 'bad' }, { label: 'Spec Serviced', field: 'highSpeciallyServicedCount', tone: 'warn' }, { label: 'With Losses', field: 'poolsWithLossesCount', tone: 'bad' }] },
    { id: 'll-maturity-monitor', tab: 'credit-loans', title: 'LL Maturity & Amendment', iconColor: 'text-sky-400', schedule: '7:45 AM ET weekdays',
      metrics: [{ label: 'Maturing ≤1y', field: 'maturingWithinYear', tone: 'warn' }, { label: 'Watchlist', field: 'watchlistCount', tone: 'bad' }, { label: 'Pending Amend', field: 'pendingAmendmentCount' }] },
    { id: 'cds-roll-monitor', tab: 'credit-loans', title: 'CDS Roll & Credit-Event', iconColor: 'text-violet-400', schedule: '8:00 AM ET weekdays',
      metrics: [{ label: 'Contracts', field: 'contractsMonitored' }, { label: 'Maturing', field: 'maturingSoonCount', tone: 'warn' }, { label: 'Coupon Soon', field: 'couponSoonCount' }, { label: 'Credit Events', field: 'creditEventCount', tone: 'bad' }] },
    { id: 'pe-capital-call-monitor', tab: 'alternatives', title: 'PE Capital Call Monitor', iconColor: 'text-emerald-400', schedule: '6:00 AM ET weekdays',
      metrics: [{ label: 'Overdue', field: 'overdueCount', tone: 'bad' }, { label: 'Upcoming (≤14d)', field: 'upcomingCount', tone: 'warn' }, { label: 'Recallable', field: 'recallableCount' }] },
    { id: 'pe-pacing-monitor', tab: 'alternatives', title: 'PE Pacing & NAV Monitor', iconColor: 'text-indigo-400', schedule: '6:15 AM ET weekdays',
      metrics: [{ label: 'Funds', field: 'fundsMonitored' }, { label: 'Period Ending', field: 'investmentPeriodEndingSoonCount', tone: 'warn' }, { label: 'Stale NAV', field: 'staleNavCount', tone: 'bad' }, { label: 'Unfunded', field: 'unfundedCommitmentCount' }] },
    { id: 're-debt-monitor', tab: 'alternatives', title: 'RE Debt Covenant & Maturity', iconColor: 'text-orange-400', schedule: '6:30 AM ET weekdays',
      metrics: [{ label: 'DSCR Breach', field: 'dscrBreachCount', tone: 'bad' }, { label: 'LTV Breach', field: 'ltvBreachCount', tone: 'bad' }, { label: 'Maturing ≤1y', field: 'maturingWithinYearCount', tone: 'warn' }, { label: 'Construction', field: 'constructionFundingCount' }] },
    { id: 're-equity-monitor', tab: 'alternatives', title: 'RE Equity Valuation & Occupancy', iconColor: 'text-teal-400', schedule: '6:45 AM ET weekdays',
      metrics: [{ label: 'Properties', field: 'propertiesMonitored' }, { label: 'Stale Val', field: 'staleValuationCount', tone: 'warn' }, { label: 'Low Occupancy', field: 'lowOccupancyCount', tone: 'bad' }] },
  ];

  const renderMonitor = (cfg: MonitorCfg) => {
    const report = monitorReports[cfg.id];
    const metrics = cfg.metrics.map(m => {
      const v = report ? (report[m.field] ?? 0) : '—';
      const has = typeof v === 'number' && v > 0;
      const color = has ? (m.tone === 'bad' ? 'text-red-400' : m.tone === 'warn' ? 'text-amber-400' : 'text-white') : 'text-white';
      return { label: m.label, value: v, color };
    });
    return (
      <AISummaryPanel
        key={cfg.id}
        icon={agentConfig[cfg.id]?.icon || <Cpu className="w-5 h-5 text-slate-400" />}
        iconColor={cfg.iconColor}
        title={cfg.title}
        summary={report?.executiveSummary ?? null}
        metrics={metrics}
        agentId={cfg.id}
        onTrigger={() => triggerAgent(cfg.id)}
        triggering={triggeringAgent === cfg.id}
        emptyMessage={`No ${cfg.title} report yet.`}
        schedule={cfg.schedule}
      />
    );
  };

  if (loading && !dashboard) return (
    <div className="min-h-[calc(100vh-64px)] bg-slate-950 flex items-center justify-center">
      <div className="text-center"><Loader2 className="w-8 h-8 text-blue-400 animate-spin mx-auto mb-4" /><p className="text-slate-400">Loading autonomous agents...</p></div>
    </div>
  );

  if (error && !dashboard) return (
    <div className="min-h-[calc(100vh-64px)] bg-slate-950 flex items-center justify-center">
      <div className="text-center">
        <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-4" />
        <p className="text-red-400 mb-4">{error}</p>
        <button onClick={fetchData} className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600">Retry</button>
      </div>
    </div>
  );

  return (
    <div className="min-h-[calc(100vh-64px)] bg-slate-950 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-3">
              <Cpu className="w-7 h-7 text-blue-400" />Autonomous Agents
            </h1>
            <p className="text-slate-400 mt-1">AI agents running continuously to monitor and optimize your portfolios</p>
          </div>
          <div className="flex items-center gap-3">
            {dashboard && (
              <div className={cn('px-3 py-1.5 rounded-lg text-sm font-medium',
                dashboard.healthStatus === 'GOOD' ? 'bg-emerald-500/20 text-emerald-400' :
                dashboard.healthStatus === 'WARNING' ? 'bg-amber-500/20 text-amber-400' : 'bg-red-500/20 text-red-400')}>
                Health: {dashboard.healthScore}%
              </div>
            )}
            <button onClick={fetchData} disabled={loading} className="flex items-center gap-2 px-4 py-2 bg-slate-800 border border-white/10 rounded-lg text-slate-300 hover:bg-slate-700 transition-all">
              <RefreshCw className={cn('w-4 h-4', loading && 'animate-spin')} />Refresh
            </button>
          </div>
        </div>

        {/* Top-level tabs: Portfolio / Alternatives / Credit & Loans */}
        <div className="flex gap-1 mb-6 bg-slate-900 border border-white/10 rounded-xl p-1 w-fit">
          {([
            { id: 'portfolio',     label: 'Portfolio',       icon: <BarChart3 className="w-4 h-4" />,  count: portfolioAgents.length },
            { id: 'alternatives',  label: 'Alternatives',    icon: <Building2 className="w-4 h-4" />,  count: alternativesAgents.length },
            { id: 'credit-loans',  label: 'Credit & Loans',  icon: <Briefcase className="w-4 h-4" />,  count: creditAgents.length },
          ] as const).map(tab => (
            <button key={tab.id} onClick={() => setTopTab(tab.id)}
              className={cn('flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium transition-all',
                topTab === tab.id ? 'bg-blue-500/20 text-blue-400' : 'text-slate-400 hover:text-white hover:bg-white/5')}>
              {tab.icon}{tab.label}
              {tab.count > 0 && (
                <span className={cn('text-[10px] px-1.5 py-0.5 rounded-full font-semibold',
                  topTab === tab.id ? 'bg-blue-500/30 text-blue-300' : 'bg-slate-700 text-slate-400')}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">

          {/* ─── PORTFOLIO TAB ─── */}
          {topTab === 'portfolio' && (
            <motion.div key="portfolio" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}>
              {/* Agent cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                {portfolioAgents.map(agent => (
                  <AgentCard key={agent.agentId} agent={agent}
                    icon={agentConfig[agent.agentId]?.icon || <Cpu className="w-5 h-5 text-slate-400" />}
                    color={agentConfig[agent.agentId]?.color || 'bg-slate-500/20'}
                    onTrigger={() => triggerAgent(agent.agentId)}
                    loading={triggeringAgent === agent.agentId} />
                ))}
              </div>

              {/* Quick stats */}
              {dashboard && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-slate-900 border border-white/10 rounded-xl p-4">
                    <div className="flex items-center gap-2 text-slate-400 text-sm mb-2"><Shield className="w-4 h-4" />Regulatory</div>
                    <div className="text-2xl font-bold text-white">{dashboard.regulatory?.violations ?? 0}</div>
                    <div className="text-xs text-slate-500">violations found</div>
                  </div>
                  <div className="bg-slate-900 border border-white/10 rounded-xl p-4">
                    <div className="flex items-center gap-2 text-slate-400 text-sm mb-2"><Calendar className="w-4 h-4" />Deadlines</div>
                    <div className="text-2xl font-bold text-amber-400">{dashboard.regulatory?.upcomingDeadlines ?? 0}</div>
                    <div className="text-xs text-slate-500">upcoming</div>
                  </div>
                  <div className="bg-slate-900 border border-white/10 rounded-xl p-4">
                    <div className="flex items-center gap-2 text-slate-400 text-sm mb-2"><DollarSign className="w-4 h-4" />Uninvested Cash</div>
                    <div className="text-2xl font-bold text-emerald-400">${((dashboard.cashOptimization?.totalUninvestedCash ?? 0) / 1000000).toFixed(1)}M</div>
                    <div className="text-xs text-slate-500">{(dashboard.cashOptimization?.aggregateCashPercent ?? 0).toFixed(1)}% of AUM</div>
                  </div>
                  <div className="bg-slate-900 border border-white/10 rounded-xl p-4">
                    <div className="flex items-center gap-2 text-slate-400 text-sm mb-2"><TrendingUp className="w-4 h-4" />Opportunities</div>
                    <div className="text-2xl font-bold text-blue-400">{dashboard.cashOptimization?.opportunities ?? 0}</div>
                    <div className="text-xs text-slate-500">+${((dashboard.cashOptimization?.potentialYieldPickup ?? 0) / 1000).toFixed(0)}K yield</div>
                  </div>
                </div>
              )}

              {/* Portfolio subtabs */}
              <div className="flex flex-wrap gap-2 mb-4 border-b border-white/10 pb-2">
                {([
                  { id: 'overview',    label: 'Overview',          icon: <BarChart3 className="w-4 h-4" /> },
                  { id: 'briefing',    label: 'Morning Briefing',  icon: <Sun className="w-4 h-4" /> },
                  { id: 'settlement',  label: 'Settlement',        icon: <CreditCard className="w-4 h-4" /> },
                  { id: 'regulatory',  label: 'Regulatory',        icon: <Shield className="w-4 h-4" /> },
                  { id: 'cash',        label: 'Cash Optimization', icon: <DollarSign className="w-4 h-4" /> },
                ] as const).map(tab => (
                  <button key={tab.id} onClick={() => setPortfolioTab(tab.id)}
                    className={cn('flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap',
                      portfolioTab === tab.id ? 'bg-blue-500/20 text-blue-400' : 'text-slate-400 hover:text-white hover:bg-white/5')}>
                    {tab.icon}{tab.label}
                  </button>
                ))}
              </div>

              <AnimatePresence mode="wait">
                {portfolioTab === 'overview' && dashboard && (
                  <motion.div key="overview" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
                    className="bg-slate-900 border border-white/10 rounded-xl p-6">
                    <h3 className="text-white font-medium mb-4 flex items-center gap-2"><Activity className="w-5 h-5 text-blue-400" />System Overview</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="text-slate-400 text-sm mb-3">Active Agents</h4>
                        <div className="space-y-2">
                          {agentStatus?.agents?.map(agent => (
                            <div key={agent.agentId} className="flex items-center justify-between bg-slate-800/50 rounded-lg p-3">
                              <div className="flex items-center gap-3">
                                {agentConfig[agent.agentId]?.icon}
                                <div>
                                  <span className="text-white text-sm">{agent.agentName}</span>
                                  <div className="text-[11px] text-slate-500">{formatAgentRunTime(agent.lastRunAt)}</div>
                                </div>
                              </div>
                              <StatusBadge status={agent.status || agent.lastRunStatus || String(agent.hasLatestReport)} />
                            </div>
                          ))}
                        </div>
                      </div>
                      <div>
                        <h4 className="text-slate-400 text-sm mb-3">Key Metrics</h4>
                        <div className="space-y-3">
                          <div className="flex items-center justify-between bg-slate-800/50 rounded-lg p-3">
                            <span className="text-slate-300 text-sm">Health Score</span>
                            <span className={cn('font-bold', dashboard.healthScore >= 80 ? 'text-emerald-400' : dashboard.healthScore >= 60 ? 'text-amber-400' : 'text-red-400')}>{dashboard.healthScore}%</span>
                          </div>
                          <div className="flex items-center justify-between bg-slate-800/50 rounded-lg p-3">
                            <span className="text-slate-300 text-sm">Regulatory Warnings</span>
                            <span className="text-amber-400 font-bold">{dashboard.regulatory?.warnings ?? 0}</span>
                          </div>
                          <div className="flex items-center justify-between bg-slate-800/50 rounded-lg p-3">
                            <span className="text-slate-300 text-sm">Cash Optimization Score</span>
                            <span className="text-emerald-400 font-bold">{dashboard.cashOptimization?.opportunities ?? 0} opportunities</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}

                {portfolioTab === 'briefing' && (
                  <motion.div key="briefing" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                    {morningBriefing && (morningBriefing.executiveSummary || morningBriefing.alerts || morningBriefing.actionItems) ? (
                      <div className="space-y-4">
                        <div className="bg-slate-900 border border-white/10 rounded-xl p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <Sun className="w-5 h-5 text-amber-400" />
                            <h3 className="text-white font-medium">Executive Summary</h3>
                          </div>
                          <div className="bg-slate-800 rounded-lg p-3 border border-white/5">
                            <p className="text-slate-300 text-sm leading-relaxed whitespace-pre-line">{morningBriefing.executiveSummary}</p>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                          <div className="bg-slate-900 border border-white/10 rounded-xl p-4">
                            <h3 className="text-white font-medium mb-3 flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-400" />Alerts ({morningBriefing.alerts?.length || 0})</h3>
                            <div className="space-y-2 max-h-64 overflow-y-auto">
                              {!morningBriefing.alerts?.length ? <p className="text-slate-500 text-sm">No alerts</p> :
                                morningBriefing.alerts?.map((alert, i) => (
                                  <div key={i} className="bg-slate-800/50 rounded-lg p-3">
                                    <div className="flex items-center justify-between mb-1"><span className="text-xs text-slate-500">{alert.category}</span><SeverityBadge severity={alert.severity} /></div>
                                    <p className="text-sm text-slate-300 font-medium">{alert.title}</p>
                                    <p className="text-xs text-slate-400 mt-1">{alert.description}</p>
                                  </div>
                                ))}
                            </div>
                          </div>
                          <div className="bg-slate-900 border border-white/10 rounded-xl p-4">
                            <h3 className="text-white font-medium mb-3 flex items-center gap-2"><Zap className="w-4 h-4 text-blue-400" />Recommended Actions ({morningBriefing.actionItems?.length || 0})</h3>
                            <div className="space-y-2 max-h-64 overflow-y-auto">
                              {!morningBriefing.actionItems?.length ? <p className="text-slate-500 text-sm">No actions recommended</p> :
                                morningBriefing.actionItems?.map((action, i) => (
                                  <div key={i} className="bg-slate-800/50 rounded-lg p-3">
                                    <div className="flex items-center gap-2 mb-1">
                                      <span className={cn('text-xs px-2 py-0.5 rounded-full font-medium',
                                        action.priority === 'CRITICAL' || action.priority === 'HIGH' ? 'bg-red-500/20 text-red-400' :
                                        action.priority === 'MEDIUM' ? 'bg-amber-500/20 text-amber-400' :
                                        'bg-blue-500/20 text-blue-400')}>{action.priority}</span>
                                      <span className="text-sm text-white flex-1">{action.title}</span>
                                    </div>
                                    <p className="text-xs text-slate-400">{action.description}</p>
                                  </div>
                                ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-slate-900 border border-white/10 rounded-xl p-8 text-center">
                        <Sun className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                        <p className="text-slate-400">No morning briefing available yet.</p>
                        <p className="text-sm text-slate-500 mt-1">Runs automatically at 7 AM on weekdays.</p>
                        <button onClick={() => triggerAgent('morning-briefing')} disabled={triggeringAgent === 'morning-briefing'} className="mt-4 px-4 py-2 bg-amber-500/20 text-amber-400 rounded-lg hover:bg-amber-500/30 transition-all">
                          {triggeringAgent === 'morning-briefing' ? <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> : <Play className="w-4 h-4 inline mr-2" />}Run Now
                        </button>
                      </div>
                    )}
                  </motion.div>
                )}

                {portfolioTab === 'settlement' && (
                  <motion.div key="settlement" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                    {settlementReport ? (
                      <div className="space-y-4">
                        <div className="bg-slate-900 border border-white/10 rounded-xl p-4">
                          <div className="grid grid-cols-4 gap-4 mb-4">
                            <div className="bg-slate-800/50 rounded-lg p-3 text-center"><div className="text-2xl font-bold text-red-400">{settlementReport.failedTrades?.length || 0}</div><div className="text-xs text-slate-500">Failed</div></div>
                            <div className="bg-slate-800/50 rounded-lg p-3 text-center"><div className="text-2xl font-bold text-amber-400">{settlementReport.pendingSettlements?.length || 0}</div><div className="text-xs text-slate-500">Pending</div></div>
                            <div className="bg-slate-800/50 rounded-lg p-3 text-center"><div className="text-2xl font-bold text-orange-400">{settlementReport.atRiskTrades?.length || 0}</div><div className="text-xs text-slate-500">At Risk</div></div>
                            <div className="bg-slate-800/50 rounded-lg p-3 text-center"><div className="text-2xl font-bold text-cyan-400">${((settlementReport.totalAtRiskValue || 0) / 1000000).toFixed(1)}M</div><div className="text-xs text-slate-500">At-Risk Value</div></div>
                          </div>
                          <div className="bg-slate-800 rounded-lg p-3 border border-white/5">
                            <p className="text-slate-300 text-sm leading-relaxed">{settlementReport.executiveSummary}</p>
                          </div>
                        </div>

                        {settlementReport.atRiskTrades && settlementReport.atRiskTrades.length > 0 && (
                          <div className="bg-slate-900 border border-white/10 rounded-xl p-4">
                            <h3 className="text-white font-medium mb-3">At-Risk Trades ({settlementReport.atRiskTrades.length})</h3>
                            <div className="space-y-2 max-h-64 overflow-y-auto">
                              {settlementReport.atRiskTrades.map((item, i) => (
                                <div key={i} className="bg-slate-800/50 rounded-lg p-3">
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="text-white font-medium">{item.trade?.securityName || 'Unknown Security'}</span>
                                    <span className={cn('text-xs px-2 py-1 rounded', item.riskScore >= 0.7 ? 'bg-red-500/20 text-red-400' : 'bg-amber-500/20 text-amber-400')}>Risk: {(item.riskScore * 100).toFixed(0)}%</span>
                                  </div>
                                  {item.riskFactors && item.riskFactors.length > 0 && (
                                    <p className="text-sm text-slate-400 mb-2">{item.riskFactors[0]}</p>
                                  )}
                                  {item.suggestedActions && item.suggestedActions.length > 0 && (
                                    <p className="text-xs text-blue-400">→ {item.suggestedActions[0]}</p>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="bg-slate-900 border border-white/10 rounded-xl p-8 text-center">
                        <CreditCard className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                        <p className="text-slate-400">No settlement report available yet.</p>
                        <button onClick={() => triggerAgent('settlement')} disabled={triggeringAgent === 'settlement'} className="mt-4 px-4 py-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-all">
                          {triggeringAgent === 'settlement' ? <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> : <Play className="w-4 h-4 inline mr-2" />}Run Now
                        </button>
                      </div>
                    )}
                  </motion.div>
                )}

                {portfolioTab === 'regulatory' && (
                  <motion.div key="regulatory" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                    {regulatoryReport ? (
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div className="bg-slate-900 border border-white/10 rounded-xl p-4">
                          <h3 className="text-white font-medium mb-3 flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-red-400" />Violations ({dashboard?.regulatory?.violations ?? 0})</h3>
                          <div className="space-y-2 max-h-64 overflow-y-auto">
                            {!regulatoryReport?.violations?.length ? (
                              <div className="text-center py-4"><CheckCircle2 className="w-8 h-8 text-emerald-400 mx-auto mb-2" /><p className="text-emerald-400 text-sm">No violations found</p></div>
                            ) : regulatoryReport.violations?.map((v, i) => (
                              <div key={i} className="bg-slate-800/50 rounded-lg p-3">
                                <div className="flex items-center justify-between mb-1"><span className="text-white font-medium">{v.ruleName}</span><SeverityBadge severity={v.severity} /></div>
                                <p className="text-sm text-slate-400">{v.description}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div className="bg-slate-900 border border-white/10 rounded-xl p-4">
                          <h3 className="text-white font-medium mb-3 flex items-center gap-2"><Calendar className="w-4 h-4 text-blue-400" />Upcoming Deadlines ({dashboard?.regulatory?.upcomingDeadlines ?? 0})</h3>
                          <div className="space-y-2">
                            {!regulatoryReport?.upcomingDeadlines?.length ? <p className="text-slate-500 text-sm">No upcoming deadlines</p> :
                              regulatoryReport.upcomingDeadlines?.map((d, i) => (
                                <div key={i} className="bg-slate-800/50 rounded-lg p-3 flex items-center justify-between">
                                  <div><span className="text-white">{d.reportType}</span><p className="text-xs text-slate-500">{new Date(d.deadline).toLocaleDateString()}</p></div>
                                  <StatusBadge status={d.status} />
                                </div>
                              ))}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-slate-900 border border-white/10 rounded-xl p-8 text-center">
                        <Shield className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                        <p className="text-slate-400">No regulatory report available yet.</p>
                        <button onClick={() => triggerAgent('regulatory-watchdog')} disabled={triggeringAgent === 'regulatory-watchdog'} className="mt-4 px-4 py-2 bg-violet-500/20 text-violet-400 rounded-lg hover:bg-violet-500/30 transition-all">
                          {triggeringAgent === 'regulatory-watchdog' ? <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> : <Play className="w-4 h-4 inline mr-2" />}Run Now
                        </button>
                      </div>
                    )}
                  </motion.div>
                )}

                {portfolioTab === 'cash' && (
                  <motion.div key="cash" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                    {cashReport || dashboard ? (
                      <div className="bg-slate-900 border border-white/10 rounded-xl p-4">
                        <div className="grid grid-cols-2 gap-4 mb-4">
                          <div className="bg-slate-800/50 rounded-lg p-3 text-center"><div className="text-2xl font-bold text-emerald-400">${((cashReport?.totalUninvestedCash ?? dashboard?.cashOptimization?.totalUninvestedCash ?? 0) / 1000000).toFixed(1)}M</div><div className="text-xs text-slate-500">Total Uninvested Cash</div></div>
                          <div className="bg-slate-800/50 rounded-lg p-3 text-center"><div className="text-2xl font-bold text-blue-400">${((cashReport?.potentialYieldPickup ?? dashboard?.cashOptimization?.potentialYieldPickup ?? 0) / 1000).toFixed(0)}K</div><div className="text-xs text-slate-500">Potential Yield Pickup</div></div>
                        </div>
                        <h3 className="text-white font-medium mb-3">Opportunities ({cashReport?.opportunities?.length ?? dashboard?.cashOptimization?.opportunities ?? 0})</h3>
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                          {!cashReport?.opportunities?.length ? <p className="text-slate-500 text-sm">Run the agent to see detailed opportunities</p> :
                            cashReport.opportunities?.map((opp, i) => (
                              <div key={i} className="bg-slate-800/50 rounded-lg p-3">
                                <div className="flex items-center justify-between mb-1"><span className="text-white font-medium">{opp.portfolioName}</span><span className="text-emerald-400 text-sm">${((opp.amount ?? 0) / 1000000).toFixed(2)}M</span></div>
                                <p className="text-sm text-slate-400">{opp.description}</p>
                                <p className="text-xs text-blue-400 mt-1">{opp.opportunityType?.replace(/_/g, ' ')} · Expected return: {(opp.expectedReturn ?? 0).toFixed(2)}%</p>
                              </div>
                            ))}
                        </div>
                      </div>
                    ) : (
                      <div className="bg-slate-900 border border-white/10 rounded-xl p-8 text-center">
                        <DollarSign className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                        <p className="text-slate-400">No cash optimization report available yet.</p>
                        <button onClick={() => triggerAgent('cash-optimization')} disabled={triggeringAgent === 'cash-optimization'} className="mt-4 px-4 py-2 bg-emerald-500/20 text-emerald-400 rounded-lg hover:bg-emerald-500/30 transition-all">
                          {triggeringAgent === 'cash-optimization' ? <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> : <Play className="w-4 h-4 inline mr-2" />}Run Now
                        </button>
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          )}

          {/* ─── ALTERNATIVES TAB ─── */}
          {topTab === 'alternatives' && (
            <motion.div key="alternatives" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}>
              {/* Agent cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                {alternativesAgents.map(agent => (
                  <AgentCard key={agent.agentId} agent={agent}
                    icon={agentConfig[agent.agentId]?.icon || <Cpu className="w-5 h-5 text-slate-400" />}
                    color={agentConfig[agent.agentId]?.color || 'bg-slate-500/20'}
                    onTrigger={() => triggerAgent(agent.agentId)}
                    loading={triggeringAgent === agent.agentId} />
                ))}
              </div>

              {/* Alternatives subtabs */}
              <div className="flex flex-wrap gap-2 mb-4 border-b border-white/10 pb-2">
                {([
                  { id: 'covenant',     label: 'Covenant Deadlines',      icon: <FileWarning className="w-4 h-4" /> },
                  { id: 'drawdown',     label: 'Drawdown & Funding',      icon: <Landmark className="w-4 h-4" /> },
                  { id: 'accrual',      label: 'Accrual / PIK',           icon: <Calculator className="w-4 h-4" /> },
                  { id: 'deterioration',label: 'Borrower Deterioration',  icon: <TrendingDown className="w-4 h-4" /> },
                  { id: 'pe-capital-call-monitor', label: 'PE Capital Calls', icon: <Banknote className="w-4 h-4" /> },
                  { id: 'pe-pacing-monitor',       label: 'PE Pacing & NAV',  icon: <PieChart className="w-4 h-4" /> },
                  { id: 're-debt-monitor',         label: 'RE Debt',          icon: <Home className="w-4 h-4" /> },
                  { id: 're-equity-monitor',       label: 'RE Equity',        icon: <Building2 className="w-4 h-4" /> },
                ] as const).map(tab => (
                  <button key={tab.id} onClick={() => setAltTab(tab.id)}
                    className={cn('flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap',
                      altTab === tab.id ? 'bg-blue-500/20 text-blue-400' : 'text-slate-400 hover:text-white hover:bg-white/5')}>
                    {tab.icon}{tab.label}
                  </button>
                ))}
              </div>

              <AnimatePresence mode="wait">
                {altTab === 'covenant' && (
                  <AISummaryPanel
                    key="covenant"
                    icon={<FileWarning className="w-5 h-5 text-orange-400" />}
                    iconColor="text-orange-400"
                    title="Covenant Deadline Watchdog"
                    summary={covenantReport?.executiveSummary ?? null}
                    metrics={[
                      { label: 'Overdue Tests', value: covenantReport?.overdueCount ?? '—', color: covenantReport?.overdueCount ? 'text-red-400' : 'text-white' },
                      { label: 'Due Within 30 Days', value: covenantReport?.dueSoonCount ?? '—', color: covenantReport?.dueSoonCount ? 'text-amber-400' : 'text-white' },
                      { label: 'Total Monitored', value: covenantReport?.items?.length ?? '—', color: 'text-slate-300' },
                    ]}
                    agentId="covenant-deadline-watchdog"
                    onTrigger={() => triggerAgent('covenant-deadline-watchdog')}
                    triggering={triggeringAgent === 'covenant-deadline-watchdog'}
                    emptyMessage="No covenant deadline report yet."
                    schedule="6:30 AM ET weekdays"
                  />
                )}

                {altTab === 'drawdown' && (
                  <AISummaryPanel
                    key="drawdown"
                    icon={<Landmark className="w-5 h-5 text-cyan-400" />}
                    iconColor="text-cyan-400"
                    title="Drawdown & Funding Monitor"
                    summary={drawdownReport?.executiveSummary ?? null}
                    metrics={[
                      { label: 'Recent Draws (7d)', value: drawdownReport?.recentActivityCount ?? '—', color: drawdownReport?.recentActivityCount ? 'text-blue-400' : 'text-white' },
                      { label: 'DDTLs Expiring (30d)', value: drawdownReport?.expiringDdtlCount ?? '—', color: drawdownReport?.expiringDdtlCount ? 'text-amber-400' : 'text-white' },
                      { label: 'High Unfunded (>$5M)', value: drawdownReport?.highUnfundedCount ?? '—', color: drawdownReport?.highUnfundedCount ? 'text-orange-400' : 'text-white' },
                    ]}
                    agentId="drawdown-funding-monitor"
                    onTrigger={() => triggerAgent('drawdown-funding-monitor')}
                    triggering={triggeringAgent === 'drawdown-funding-monitor'}
                    emptyMessage="No drawdown monitor report yet."
                    schedule="7:30 AM ET weekdays"
                  />
                )}

                {altTab === 'accrual' && (
                  <AISummaryPanel
                    key="accrual"
                    icon={<Calculator className="w-5 h-5 text-purple-400" />}
                    iconColor="text-purple-400"
                    title="Accrual / PIK Reconciler"
                    summary={accrualReport?.executiveSummary ?? null}
                    metrics={[
                      { label: 'Missing Accruals', value: accrualReport?.missingAccrualCount ?? '—', color: accrualReport?.missingAccrualCount ? 'text-red-400' : 'text-emerald-400' },
                      { label: 'PIK Events (3d)', value: accrualReport?.pikEventCount ?? '—', color: accrualReport?.pikEventCount ? 'text-amber-400' : 'text-white' },
                      { label: 'Non-Accrual Loans', value: accrualReport?.nonAccrualCount ?? '—', color: accrualReport?.nonAccrualCount ? 'text-red-400' : 'text-white' },
                    ]}
                    agentId="accrual-pik-reconciler"
                    onTrigger={() => triggerAgent('accrual-pik-reconciler')}
                    triggering={triggeringAgent === 'accrual-pik-reconciler'}
                    emptyMessage="No accrual reconciler report yet."
                    schedule="7:30 PM ET weekdays (after EOD accruals)"
                  />
                )}

                {altTab === 'deterioration' && (
                  <AISummaryPanel
                    key="deterioration"
                    icon={<TrendingDown className="w-5 h-5 text-rose-400" />}
                    iconColor="text-rose-400"
                    title="Borrower Deterioration Sweep"
                    summary={deteriorationReport?.executiveSummary ?? null}
                    metrics={[
                      { label: 'On Watchlist', value: deteriorationReport?.totalOnWatchlist ?? '—', color: deteriorationReport?.totalOnWatchlist ? 'text-amber-400' : 'text-white' },
                      { label: 'Critical', value: deteriorationReport?.criticalCount ?? '—', color: deteriorationReport?.criticalCount ? 'text-red-400' : 'text-white' },
                      { label: 'High', value: deteriorationReport?.highCount ?? '—', color: deteriorationReport?.highCount ? 'text-orange-400' : 'text-white' },
                    ]}
                    agentId="borrower-deterioration-sweep"
                    onTrigger={() => triggerAgent('borrower-deterioration-sweep')}
                    triggering={triggeringAgent === 'borrower-deterioration-sweep'}
                    emptyMessage="No deterioration sweep report yet."
                    schedule="8:00 AM ET weekdays"
                  />
                )}

                {altTab === 'pe-capital-call-monitor' && renderMonitor(MONITORS.find(m => m.id === 'pe-capital-call-monitor')!)}
                {altTab === 'pe-pacing-monitor' && renderMonitor(MONITORS.find(m => m.id === 'pe-pacing-monitor')!)}
                {altTab === 're-debt-monitor' && renderMonitor(MONITORS.find(m => m.id === 're-debt-monitor')!)}
                {altTab === 're-equity-monitor' && renderMonitor(MONITORS.find(m => m.id === 're-equity-monitor')!)}
              </AnimatePresence>
            </motion.div>
          )}

          {/* ─── CREDIT & LOANS TAB ─── */}
          {topTab === 'credit-loans' && (
            <motion.div key="credit-loans" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}>
              {/* Agent cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                {creditAgents.map(agent => (
                  <AgentCard key={agent.agentId} agent={agent}
                    icon={agentConfig[agent.agentId]?.icon || <Cpu className="w-5 h-5 text-slate-400" />}
                    color={agentConfig[agent.agentId]?.color || 'bg-slate-500/20'}
                    onTrigger={() => triggerAgent(agent.agentId)}
                    loading={triggeringAgent === agent.agentId} />
                ))}
              </div>
              {/* Subtabs */}
              <div className="flex flex-wrap gap-2 mb-4 border-b border-white/10 pb-2">
                {MONITORS.filter(m => m.tab === 'credit-loans').map(cfg => (
                  <button key={cfg.id} onClick={() => setCreditTab(cfg.id)}
                    className={cn('flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap',
                      creditTab === cfg.id ? 'bg-blue-500/20 text-blue-400' : 'text-slate-400 hover:text-white hover:bg-white/5')}>
                    {agentConfig[cfg.id]?.icon}{cfg.title}
                  </button>
                ))}
              </div>
              <AnimatePresence mode="wait">
                {MONITORS.filter(m => m.tab === 'credit-loans' && m.id === creditTab).map(renderMonitor)}
              </AnimatePresence>
            </motion.div>
          )}

        </AnimatePresence>
      </div>
    </div>
  );
};

export default AutonomousAgentsDashboard;
